export const photoCaptureServiceModule = angular
  .module('Q.services.photo-capture-service', [])

  .factory('photoCaptureService', function ($q, $window, $log) {
    const OVERSIZE_SENTRY_THROTTLE_MS = 10 * 60 * 1000; // 10 minutes
    let lastOversizeSentryAt = 0;
    let lastOversizeSentryKey = null;

    const sendOversizeToSentry = (message, data) => {
      try {
        if (!window.Sentry || (window.config && window.config.devMode)) {
          return;
        }

        const now = Date.now();
        const keyParts = [
          message,
          data && data.maxSizeKB,
          // Bucket size so we don't create too many unique signatures
          data && data.estimatedSizeKB
            ? Math.round(data.estimatedSizeKB / 25) * 25
            : null,
          data && data.canvasWidth,
          data && data.canvasHeight,
        ];
        const key = keyParts.join('|');

        // Rate-limit per signature.
        if (
          lastOversizeSentryKey === key &&
          now - lastOversizeSentryAt < OVERSIZE_SENTRY_THROTTLE_MS
        ) {
          return;
        }

        lastOversizeSentryKey = key;
        lastOversizeSentryAt = now;

        window.Sentry.captureMessage(message, {
          level: 'warning',
          tags: {
            feature: 'proctoring-images',
          },
          extra: data,
          fingerprint: [
            'proctoring-photo-oversize',
            String(data && data.maxSizeKB),
          ],
        });
      } catch (e) {
        // never allow observability to break proctoring flow
      }
    };
    const build = ({ onEnd }) => {
      const makeVideoElement = () => {
        const videoElement = angular.element('<video />')[0];
        videoElement.onerror = onEnd;
        return videoElement;
      };

      // This video element is public-facing and is used to show the
      // cam feed to the user. It may be destroyed on route changes
      // during normal app operation but can be re-created by the
      // client with newExternalVideo() as needed.
      // A destroyed video element manifests as a "frozen" frame.
      let externalVideo;

      // This video element is used for all internal operations, and
      // should not be exposed to the user so we can ensure that the
      // element exists for the duration of this service's operation.
      let internalVideo;

      const canvas = angular.element('<canvas />')[0];
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      let stream;

      const running = () => !!stream;

      const ensureRunning = (fn) => {
        return (...args) => {
          if (running()) {
            return fn(...args);
          }

          throw Error('Photo capture service must be running');
        };
      };

      const newExternalVideo = () => {
        if (!running()) {
          return $q.reject('Photo capture service must be running');
        }

        externalVideo = makeVideoElement();
        externalVideo.srcObject = stream;

        const p = externalVideo.play();
        // older browsers may not return a promise
        return p ? p.then(() => externalVideo) : $q.resolve(externalVideo);
      };

      const imageData = ensureRunning(() => {
        ctx.drawImage(internalVideo, 0, 0);
        return ctx.getImageData(0, 0, canvas.width, canvas.height);
      });

      const brightness = ensureRunning(() => {
        // https://stackoverflow.com/a/13763063/6243352
        const { data } = imageData();
        let total = 0;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          total += Math.floor((r + g + b) / 3);
        }

        return Math.floor(total / (canvas.width * canvas.height)) / 255;
      });

      const stop = () => {
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
          stream = null;
        }

        if (internalVideo) {
          internalVideo.srcObject = null;
        }

        if (externalVideo) {
          externalVideo.srcObject = null;
        }
      };

      const _startStream = (stream_) => {
        stream = stream_;
        stream.getVideoTracks().forEach((track) =>
          track.addEventListener('ended', () => {
            stop();

            if (angular.isFunction(onEnd)) {
              onEnd();
            }
          }),
        );
        return $q((resolve, reject) => {
          internalVideo = makeVideoElement();
          internalVideo.addEventListener('loadeddata', () => {
            if (internalVideo.readyState === internalVideo.HAVE_ENOUGH_DATA) {
              canvas.height = internalVideo.videoHeight;
              canvas.width = internalVideo.videoWidth;
              resolve();
            }
          });
          internalVideo.addEventListener('error', reject);
          internalVideo.srcObject = stream_;

          const p = internalVideo.play();
          // older browsers may not return a promise
          if (p) {
            p.catch(reject);
          }
        });
      };

      const start = () => {
        stop();

        return $q((resolve, reject) => {
          if (!$window.navigator.mediaDevices) {
            reject(
              'window.navigator.mediaDevices is not supported by this browser',
            );
            return;
          }

          $window.navigator.mediaDevices
            .getUserMedia({
              video: {
                facingMode: 'user',
                width: 640,
                height: 480,
              },
            })
            .then(_startStream)
            .then(resolve)
            .catch(reject);
        });
      };

      const takePhoto = ensureRunning((maxSizeKB = 250) => {
        // Adaptive quality control to ensure images don't exceed size limit
        // NOTE: maxSizeKB is set to 50 for testing purposes - change to 250 for production
        let quality = 0.6;
        let dataURL;
        let attempts = 0;
        let estimatedSizeKB;
        const maxAttempts = 5;
        const minQuality = 0.3;

        do {
          ctx.drawImage(internalVideo, 0, 0);
          dataURL = canvas.toDataURL('image/jpeg', quality);

          // Calculate size: base64 string length * 0.75 ≈ actual bytes
          // (base64 encoding has ~33% overhead, so we divide by 1.33 or multiply by 0.75)
          estimatedSizeKB = (dataURL.length * 0.75) / 1024;

          if (estimatedSizeKB <= maxSizeKB || quality <= minQuality) {
            if (quality <= minQuality && estimatedSizeKB > maxSizeKB) {
              const data = {
                estimatedSizeKB: Math.round(estimatedSizeKB),
                maxSizeKB,
                quality,
                attempts,
                canvasWidth: canvas.width,
                canvasHeight: canvas.height,
                videoWidth: internalVideo ? internalVideo.videoWidth : null,
                videoHeight: internalVideo ? internalVideo.videoHeight : null,
              };

              $log.warn(
                'Proctoring photo exceeds size limit after compression',
                data,
              );
              sendOversizeToSentry(
                'Proctoring photo exceeds size limit after compression',
                data,
              );
            }
            // Size is acceptable or we've reached minimum quality
            return dataURL;
          }

          // Reduce quality by 15% for next attempt
          quality = Math.max(minQuality, quality * 0.85);
          attempts++;
        } while (attempts < maxAttempts);

        if (estimatedSizeKB > maxSizeKB) {
          const data = {
            estimatedSizeKB: Math.round(estimatedSizeKB),
            maxSizeKB,
            quality,
            attempts,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            videoWidth: internalVideo ? internalVideo.videoWidth : null,
            videoHeight: internalVideo ? internalVideo.videoHeight : null,
          };

          $log.warn(
            'Proctoring photo exceeds size limit after maxAttempts',
            data,
          );
          sendOversizeToSentry(
            'Proctoring photo exceeds size limit after maxAttempts',
            data,
          );
        }

        // Return whatever we have after max attempts
        return dataURL;
      });

      return {
        start,
        stop,
        imageData,
        running,
        brightness,
        takePhoto,
        newExternalVideo,
      };
    };
    return {
      build,
    };
  });
